"use client";

import { useState, useSyncExternalStore } from "react";
import { Heart } from "lucide-react";

import { incrementLoveCount } from "@/actions/love-count";
import { cn } from "@/lib/utils";

// sessionStorage never changes under us, so subscribe is a no-op.
const emptySubscribe = () => () => {};

const LoveButton = () => {
  const likedInSession = useSyncExternalStore(
    emptySubscribe,
    () => sessionStorage.getItem("hasLiked") === "true",
    () => false,
  );
  const [justLiked, setJustLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pop, setPop] = useState(false);

  const hasLiked = likedInSession || justLiked;

  const handleLike = async () => {
    if (hasLiked || isLoading) return;
    setIsLoading(true);
    setPop(true);
    try {
      const result = await incrementLoveCount();
      if (result.success) {
        setJustLiked(true);
        sessionStorage.setItem("hasLiked", "true");
        const el = document.getElementById("love-count");
        if (el && result.count !== undefined) {
          el.textContent = result.count.toString();
        }
      }
    } catch (error) {
      console.error("Failed to update love count:", error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setPop(false), 650);
    }
  };

  return (
    <button
      type="button"
      onClick={handleLike}
      disabled={hasLiked || isLoading}
      className={cn(
        "relative inline-flex items-center gap-2 overflow-hidden rounded-full border px-4 py-1.5 text-xs font-medium transition-all duration-300",
        hasLiked
          ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
          : "border-hairline text-fg-muted hover:border-hairline-strong hover:text-fg",
        pop && "scale-105",
      )}
    >
      {/* rose glow that pulses on click */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 bg-rose-500/25 opacity-0",
          pop && "animate-pulse opacity-100 motion-reduce:animate-none",
        )}
      />

      <span className="relative grid place-items-center">
        {pop && (
          <Heart
            aria-hidden
            className="absolute size-3.5 animate-ping fill-rose-500 text-rose-500 motion-reduce:hidden"
          />
        )}
        <Heart
          aria-hidden
          className={cn(
            "size-3.5 transition-all",
            hasLiked && "fill-rose-500 text-rose-500",
            pop && "scale-125",
          )}
        />
      </span>

      <span className="relative">
        {hasLiked ? "Thank you, much appreciated!" : "Appreciate this"}
      </span>

      {isLoading && !hasLiked && (
        <span className="relative animate-spin motion-reduce:animate-none">
          ⋯
        </span>
      )}
    </button>
  );
};

export default LoveButton;
